import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useJobs } from '../context/JobsContext';
import { FaMapMarkerAlt, FaClock, FaArrowLeft } from 'react-icons/fa';
import ContactCTA from '../components/ContactCTA';
import Toast from '../components/Toast';
import '../styles/JobApplication.css';

const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
  'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos',
  'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi',
  'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova',
  'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
  'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan',
  'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const JobApplication = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { getActiveJobs } = useJobs();
  const allJobs = getActiveJobs();
  const job = allJobs.find(j => j.id === parseInt(jobId) || j.id === jobId);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    yearsOfExperience: '',
    currentLocation: '',
    country: '',
    availability: '',
    aboutYourself: '',
    resume: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      resume: e.target.files[0]
    }));
    if (errors.resume) {
      setErrors(prev => ({
        ...prev,
        resume: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.yearsOfExperience) {
      newErrors.yearsOfExperience = 'Years of experience is required';
    }
    if (!formData.currentLocation.trim()) {
      newErrors.currentLocation = 'Current location is required';
    }
    if (!formData.country) {
      newErrors.country = 'Country is required';
    }
    if (!formData.availability) {
      newErrors.availability = 'Availability is required';
    }
    if (!formData.aboutYourself.trim()) {
      newErrors.aboutYourself = 'Please tell us about yourself';
    }

    const resumeInput = document.getElementById('resume');
    if (!resumeInput || !resumeInput.files || resumeInput.files.length === 0) {
      newErrors.resume = 'Resume is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData to send file along with other data
      const submitData = new FormData();
      submitData.append('applicantEmail', formData.email);
      submitData.append('applicantName', `${formData.firstName} ${formData.lastName}`);
      submitData.append('jobTitle', job.title);
      submitData.append('phone', formData.phone);
      submitData.append('currentLocation', formData.currentLocation);
      submitData.append('country', formData.country);
      submitData.append('yearsOfExperience', formData.yearsOfExperience);
      submitData.append('availability', formData.availability);
      submitData.append('aboutYourself', formData.aboutYourself);

      // Append the resume file
      const resumeInput = document.getElementById('resume');
      if (resumeInput && resumeInput.files && resumeInput.files[0]) {
        submitData.append('resume', resumeInput.files[0]);
      }

      // Send application with resume
      const emailResponse = await fetch('/api/send-confirmation-email', {
        method: 'POST',
        body: submitData
      });

      const emailResult = await emailResponse.json();

      if (emailResult.success) {
        if (emailResult.emailSent) {
          setToast({ isVisible: true, message: 'Your application has been submitted successfully! A confirmation email has been sent to ' + formData.email, type: 'success' });
          setTimeout(() => navigate('/careers'), 2000);
        } else {
          setToast({ isVisible: true, message: 'Your application has been submitted successfully! Our team will review your application and contact you soon.', type: 'success' });
          setTimeout(() => navigate('/careers'), 2000);
        }
      } else {
        setToast({ isVisible: true, message: 'There was an error submitting your application. Please try again or contact us directly at info@joywcs.com', type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setToast({ isVisible: true, message: 'There was an error submitting your application. Please try again or contact us directly at info@joywcs.com', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!job) {
    return (
      <div className="job-application-page">
        <Toast 
          message={toast.message} 
          type={toast.type} 
          isVisible={toast.isVisible} 
          onClose={() => setToast({ ...toast, isVisible: false })}
        />
        <section className="page-hero">
          <div className="container">
            <h1>Job Not Found</h1>
            <p>The position you're looking for is no longer available</p>
          </div>
        </section>
        <div className="container">
          <div className="not-found-content">
            <p>This job posting may have been removed or filled.</p>
            <button className="btn btn-primary" onClick={() => navigate('/careers')}>
              <FaArrowLeft /> Back to Careers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="job-application-page">
      <Toast 
        message={toast.message} 
        type={toast.type} 
        isVisible={toast.isVisible} 
        onClose={() => setToast({ ...toast, isVisible: false })}
      />
      <section className="page-hero">
        <div className="container">
          <h1>Apply for Position</h1>
          <p>Join our team at JWCS</p>
        </div>
      </section>

      <section className="job-details-section">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/careers')}>
            <FaArrowLeft /> Back to Careers
          </button>

          <div className="job-info-card">
            <h2>{job.title}</h2>
            <div className="job-meta">
              <span className="job-location">
                <FaMapMarkerAlt /> {job.location}
              </span>
              <span className="job-department">{job.department}</span>
              <span className="job-type">
                <FaClock /> {job.type}
              </span>
            </div>
            {job.description && (
              <p className="job-description">{job.description}</p>
            )}
          </div>
        </div>
      </section>

      <section className="application-form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-header">
              <h2>Submit Your Application</h2>
              <p>Fill out the form below to apply for this position</p>
            </div>

            <form onSubmit={handleSubmitApplication} className="application-form" noValidate>
              <div className="form-row">
                <div className={`form-group ${errors.firstName ? 'has-error' : ''}`}>
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>
                <div className={`form-group ${errors.lastName ? 'has-error' : ''}`}>
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
                <div className={`form-group ${errors.phone ? 'has-error' : ''}`}>
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className={`form-group ${errors.yearsOfExperience ? 'has-error' : ''}`}>
                  <label htmlFor="yearsOfExperience">Years of Experience *</label>
                  <select
                    id="yearsOfExperience"
                    name="yearsOfExperience"
                    value={formData.yearsOfExperience}
                    onChange={handleInputChange}
                  >
                    <option value="">Select experience</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                  {errors.yearsOfExperience && <span className="error-message">{errors.yearsOfExperience}</span>}
                </div>
                <div className={`form-group ${errors.currentLocation ? 'has-error' : ''}`}>
                  <label htmlFor="currentLocation">Current Location (City, State) *</label>
                  <input
                    type="text"
                    id="currentLocation"
                    name="currentLocation"
                    value={formData.currentLocation}
                    onChange={handleInputChange}
                    placeholder="City, State"
                  />
                  {errors.currentLocation && <span className="error-message">{errors.currentLocation}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className={`form-group ${errors.country ? 'has-error' : ''}`}>
                  <label htmlFor="country">Country *</label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country, index) => (
                      <option key={index} value={country}>{country}</option>
                    ))}
                  </select>
                  {errors.country && <span className="error-message">{errors.country}</span>}
                </div>
                <div className={`form-group ${errors.availability ? 'has-error' : ''}`}>
                  <label htmlFor="availability">Availability *</label>
                  <select
                    id="availability"
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                  >
                    <option value="">Select availability</option>
                    <option value="immediate">Immediate</option>
                    <option value="2-weeks">2 Weeks Notice</option>
                    <option value="1-month">1 Month Notice</option>
                    <option value="2-months">2 Months Notice</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.availability && <span className="error-message">{errors.availability}</span>}
                </div>
              </div>

              <div className={`form-group ${errors.aboutYourself ? 'has-error' : ''}`}>
                <label htmlFor="aboutYourself">Tell Us About Yourself *</label>
                <textarea
                  id="aboutYourself"
                  name="aboutYourself"
                  value={formData.aboutYourself}
                  onChange={handleInputChange}
                  rows="5"
                  placeholder="Share your background, skills, and why you're interested in this position..."
                />
                {errors.aboutYourself && <span className="error-message">{errors.aboutYourself}</span>}
              </div>

              <div className={`form-group file-upload ${errors.resume ? 'has-error' : ''}`}>
                <label htmlFor="resume">Upload Resume *</label>
                <input
                  type="file"
                  id="resume"
                  name="resume"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                />
                <span className="file-hint">Accepted formats: PDF, DOC, DOCX (Max 5MB)</span>
                {errors.resume && <span className="error-message">{errors.resume}</span>}
              </div>

              <button
                type="submit"
                className="btn btn-primary submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <ContactCTA />
    </div>
  );
};

export default JobApplication;
